import { InspectionVM } from '../inspections';
import { EmailActivityModel } from './email-activity';
import { FormActivity, FormActivityModel } from './form-activity';

export enum InspectionStep {
  Request = 1,
  Schedule = 2,
  Complete = 3
}

export class ConsolidatedInspectionSections {
  static REQUEST_SECTION_ID = 'request-inspection';
  static SCHEDULE_SECTION_ID = 'schedule-inspection';
  static COMPLETE_SECTION_ID = 'complete-inspection';
}

/**
 * Configuration for the .ics calendar attachment that can be added to
 * a Consolidated Inspection Activity's OnScheduledEmail. Mirrors the
 * subset of AppointmentActivityModel that is meaningful here (the
 * appointment date/time is implicit — it comes from SCHEDULE_SDL_USER).
 */
export class ScheduledCalendarEventConfig {
  location = '';
  durationMinutes = '';
  allDay = false;

  constructor(options?: Partial<ScheduledCalendarEventConfig>) {
    if (options) {
      Object.assign(this, options);
    }
  }
}

export class ConsolidatedInspectionActivityModel extends FormActivityModel {
  inspectionId: string;
  inspection: InspectionVM;

  inspectionType: string;

  scheduleStepResponsibleRoleId: string;
  completeStepResponsibleRoleId: string;

  requestStepActivity: FormActivity;
  scheduleStepActivity: FormActivity;
  completeStepActivity: FormActivity;

  sendOnRequestedEmail: boolean;
  sendOnScheduledEmail: boolean;
  
  onRequestedEmailModel: EmailActivityModel;
  onScheduledEmailModel: EmailActivityModel;

  /**
   * When true, an .ics calendar event is attached to the OnScheduledEmail.
   * Only meaningful when sendOnScheduledEmail is also true.
   */
  attachScheduledCalendarEvent: boolean;
  onScheduledCalendarEvent: ScheduledCalendarEventConfig;

  noScheduleAllowed: boolean;

  /**
   * Positions (indices) into the parent activity's merged
   * `formLayoutModel.columnEntities` list identifying which Complete Step
   * column entities are flagged as "No Schedule" only. When a column entity's
   * index appears here, the workflow-manager Complete Step input will not
   * render it (it exists purely for population by the mobile inspection app
   * during a No Schedule inspection). Only settable by System Administrator
   * (DO_ANYTHING) users, and never for a column entity that contains a
   * system data entity.
   */
  noScheduleOnlyColumnEntityIndices: number[] = [];

  constructor(options?: Partial<ConsolidatedInspectionActivityModel>) {
    super(options);

    this.inspection = new InspectionVM();

    if (options && options.inspection) {
      Object.assign(this.inspection, options.inspection);
    }

    this.attachScheduledCalendarEvent =
      (options && options.attachScheduledCalendarEvent) || false;

    this.onScheduledCalendarEvent = new ScheduledCalendarEventConfig(
      options ? options.onScheduledCalendarEvent : null
    );

    this.noScheduleOnlyColumnEntityIndices =
      options && Array.isArray(options.noScheduleOnlyColumnEntityIndices)
        ? [...options.noScheduleOnlyColumnEntityIndices]
        : [];
  }
}

export class ConsolidatedInspectionActivity extends FormActivity {
  model: ConsolidatedInspectionActivityModel;
  submittedStep?: InspectionStep;
  responsibleForRequest: boolean;
  responsibleForSchedule: boolean;
  responsibleForComplete: boolean;
  requestStepSubmitted: boolean;
  scheduleStepSubmitted: boolean;

  constructor(options?: Partial<ConsolidatedInspectionActivity>) {
    super(options);

    if (options) {
      Object.assign(this, options);
    }

    this.model = new ConsolidatedInspectionActivityModel(
      options ? options.model : null
    );
  }
}
